import { Classes } from "@blueprintjs/core";
import classnames from "classnames";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { PlanProject } from "../models/plan-types";
import { PlanAbsenceOption } from "./plan-absences";
import styles from "./ProjectPicker.module.scss";

/** Отступ от края окна, чтобы окно выбора не прилипало к границе */
const VIEWPORT_GAP = 8;

/** На сколько поднять окно над строкой, если оно раскрывается вверх */
const CLICK_OFFSET = 34;

interface ProjectPickerProps {
    x: number;
    y: number;
    projects: PlanProject[];
    /** Отпуск, больничный, БС — те же типы, что в табеле */
    absences: PlanAbsenceOption[];
    /** Проект, назначенный сейчас (подсвечиваем) */
    currentId?: string | null;
    /** Тип отсутствия, стоящий сейчас (подсвечиваем его кнопку) */
    currentAbsence?: string | null;
    onPick: (orderId: string) => void;
    onPickAbsence: (absence: PlanAbsenceOption) => void;
    onClear: () => void;
    onClose: () => void;
}

/**
 * Быстрый выбор проекта. Часы не спрашиваем — плановый день всегда полный,
 * вид работ в плане тоже больше не выбирается, поэтому весь ввод — это один проект.
 * Тем же окном закрывают день отсутствием: отпуск и больничный в план попадают
 * отсюда, а не только из табеля.
 */
export const ProjectPicker: React.FC<ProjectPickerProps> = (props) => {
    const { x, y, projects, absences, currentId, currentAbsence, onPick, onPickAbsence, onClear, onClose } = props;

    const [query, setQuery] = useState("");
    const [active, setActive] = useState(0);
    const [position, setPosition] = useState({ left: x, top: y, visibility: "hidden" as const });
    const inputRef = useRef<HTMLInputElement>(null);
    const rootRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    const filtered = useMemo(() => {
        const search = query.trim().toLowerCase();

        if (!search) {
            return projects;
        }

        return projects.filter((project) =>
            `${project.fullInventoryNumber} ${project.name}`.toLowerCase().includes(search)
        );
    }, [projects, query]);

    // окно выбора не должно вылезать за экран: у нижних строк раскрываем его вверх
    useLayoutEffect(() => {
        const element = rootRef.current;

        if (!element) {
            return;
        }

        const { width, height } = element.getBoundingClientRect();

        const fitsBelow = y + height + VIEWPORT_GAP <= window.innerHeight;
        const top = fitsBelow
            ? y
            : Math.max(VIEWPORT_GAP, y - height - CLICK_OFFSET);

        const left = Math.max(VIEWPORT_GAP, Math.min(x, window.innerWidth - width - VIEWPORT_GAP));

        setPosition({ left, top, visibility: "visible" as any });
    }, [x, y]);

    useEffect(() => {
        inputRef.current && inputRef.current.focus();
    }, []);

    useEffect(() => {
        setActive(0);
    }, [query]);

    // список полный (сотни проектов) — при навигации стрелками подкручиваем к текущему
    useEffect(() => {
        const list = listRef.current;
        const item = list && (list.querySelector(`[data-index="${active}"]`) as HTMLElement | null);

        if (!list || !item) {
            return;
        }

        if (item.offsetTop < list.scrollTop) {
            list.scrollTop = item.offsetTop;
        } else if (item.offsetTop + item.offsetHeight > list.scrollTop + list.clientHeight) {
            list.scrollTop = item.offsetTop + item.offsetHeight - list.clientHeight;
        }
    }, [active]);

    useEffect(() => {
        const onPointerDown = (event: PointerEvent) => {
            if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        document.addEventListener("pointerdown", onPointerDown, true);
        return () => document.removeEventListener("pointerdown", onPointerDown, true);
    }, [onClose]);

    const onKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === "Escape") {
                event.preventDefault();
                onClose();
                return;
            }

            if (event.key === "ArrowDown") {
                event.preventDefault();
                setActive((index) => Math.min(index + 1, filtered.length - 1));
                return;
            }

            if (event.key === "ArrowUp") {
                event.preventDefault();
                setActive((index) => Math.max(index - 1, 0));
                return;
            }

            if (event.key === "Enter") {
                event.preventDefault();
                const project = filtered[active];
                project && onPick(project.id);
            }
        },
        [filtered, active, onPick, onClose]
    );

    return ReactDOM.createPortal(
        <div className={styles.picker} style={position} ref={rootRef}>
            <input
                ref={inputRef}
                className={classnames(Classes.INPUT, "planr-default-input", styles.search)}
                placeholder={`Проект (${projects.length})`}
                autoComplete="off"
                data-lpignore="true"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={onKeyDown}
            />

            <div className={styles.list} ref={listRef}>
                {filtered.map((project, index) => (
                    <div
                        key={project.id}
                        data-index={index}
                        className={classnames(styles.item, {
                            [styles.active]: index === active,
                            [styles.current]: project.id === currentId,
                        })}
                        onMouseEnter={() => setActive(index)}
                        onClick={() => onPick(project.id)}
                    >
                        <span className={styles.color} style={{ backgroundColor: project.color }} />
                        <span className={styles.number}>{project.fullInventoryNumber}</span>
                        <span className={styles.itemName}>{project.name}</span>
                    </div>
                ))}

                {!filtered.length && <div className={styles.empty}>Ничего не найдено</div>}
            </div>

            {!!absences.length && (
                <div className={styles.absences}>
                    {absences.map((absence) => (
                        <button
                            key={absence.type}
                            type="button"
                            title={absence.type}
                            className={classnames(styles.absence, {
                                [styles.absenceCurrent]: absence.type === currentAbsence,
                                [styles.absenceWide]: !!absence.wide,
                            })}
                            onClick={() => onPickAbsence(absence)}
                        >
                            {absence.icon}
                            <span className={styles.absenceLabel}>{absence.label}</span>
                        </button>
                    ))}
                </div>
            )}

            <div className={styles.footer}>
                <PlanrButton type="graybtn" size="small" onClick={onClear}>
                    Очистить
                </PlanrButton>
            </div>
        </div>,
        document.body
    );
};
